import type { MouseEvent } from 'react';
import { Icon, type IconName } from '../theme/Icon';
import s from './Rail.module.css';
import { t } from '@app/i18n';

export interface RailItem {
  /** Rota (href). O `Rail` não conhece o router: o app intercepta em `onNavigate`. */
  href: string;
  label: string;
  icon: IconName;
  /** Contador do item (ex.: aprovações pendentes). `0` não é exibido. */
  count?: number;
  /** `true` pinta o contador em verde-claro em vez de laranja (sem urgência). */
  quietCount?: boolean;
  /**
   * `true` = item de categoria, desenhado recuado sob o item-pai que o antecede.
   *
   * É **indentação, não acordeão**: o desenho do hub (spec «Acesso: pessoa ×
   * agente», §2 e §3) mostra as categorias sempre visíveis sob «Access Control».
   * Recolher esconderia justamente a hierarquia que a tela nova existe para
   * ensinar, e custaria estado (aberto/fechado, persistência, foco) que nenhum
   * outro grupo do rail pede. Item sem a marca não muda de aparência — os outros
   * três grupos continuam exatamente como estavam.
   */
  sub?: boolean;
  /**
   * `true` = rótulo de um grupo de telas, sem link (12/10/2026, «Controle de acesso»). Ele tem a forma de um item, com
   * o ícone, para os filhos recuados continuarem a se ler como dele; mas não navega, não fica ativo e não recolhe.
   */
  heading?: boolean;
}

export interface RailGroup {
  /** Título do grupo. Grupo sem título não renderiza cabeçalho. */
  title?: string;
  items: RailItem[];
}

/**
 * Item do rodapé do rail (16/09) — destino **fora da moldura do console**, aberto
 * sempre em outra aba.
 *
 * É um tipo próprio, e não um `RailItem` com uma marca, porque as três coisas que o
 * definem são o oposto das do item de menu: ele **não** é uma tela do console (não
 * entra em `ROTAS`, logo não tem rota, título nem busca), **não** fica ativo (não há
 * `aria-current`: a aba do console nunca está nele) e **não** passa por `onNavigate`
 * (o router do console não conhece o destino). Um `boolean` a mais em `RailItem`
 * carregaria três campos que nunca se aplicam.
 *
 * O `target="_blank"` é da definição, não do chamador: o console guarda estado na
 * tela (drawers, filtros, busca) e o destino tem casca própria. Um item de rodapé que
 * precise navegar por dentro é outra coisa, e quem o escrever decide então — em vez de
 * herdar em silêncio o alvo deste.
 */
export interface RailFooterItem {
  href: string;
  label: string;
  icon: IconName;
}

export interface RailProps {
  groups: RailGroup[];
  /** `href` do item ativo. */
  activeHref?: string;
  brand?: { name: string; subtitle?: string; icon?: IconName };
  onNavigate?: (href: string, e: MouseEvent<HTMLAnchorElement>) => void;
  /** Rótulo acessível da navegação. */
  label?: string;
  /**
   * Itens fixos no rodapé, abaixo de todos os grupos — ver `RailFooterItem`.
   *
   * Ficam **fora** do `<nav>` do menu, como a marca: o menu é a lista de telas do
   * console, e `.navwrap` é o que rola. Assim um rodapé não muda o que os leitores
   * do menu (`railGroups`, a busca, os testes e o `ShellPage` do e2e) enxergam.
   */
  footer?: RailFooterItem[];
}

/** Menu lateral (`.rail`) com grupos e contadores. */
export function Rail({
  groups,
  activeHref,
  brand = { name: '360 Agentic Identity' },
  onNavigate,
  label = t('common.ui.mainMenu'),
  footer = [],
}: RailProps) {
  return (
    <div className={s.rail}>
      <div className={s.brand}>
        <span className={s.mk} aria-hidden="true">
          <Icon name={brand.icon ?? 'robot'} size="lg" />
        </span>
        <span className={s.nm}>
          <span className={s.n}>{brand.name}</span>
          {brand.subtitle ? <span className={s.sub}>{brand.subtitle}</span> : null}
        </span>
      </div>

      <nav className={s.navwrap} aria-label={label}>
        {groups.map((group, gi) => (
          <div className={s.navgrp} key={group.title ?? `grupo-${gi}`}>
            {group.title ? <h2 className={s.groupTitle}>{group.title}</h2> : null}
            {group.items.map((item) => {
              if (item.heading) {
                return (
                  <span
                    key={item.href}
                    className={[s.nav, s.heading].join(' ')}
                    data-testid="rail-heading"
                  >
                    <Icon name={item.icon} size="md" />
                    <span className={s.lbl} title={item.label}>
                      {item.label}
                    </span>
                  </span>
                );
              }
              const active = item.href === activeHref;
              return (
                <a
                  key={item.href}
                  href={item.href}
                  className={[s.nav, item.sub ? s.subitem : '', active ? s.on : '']
                    .filter(Boolean)
                    .join(' ')}
                  aria-current={active ? 'page' : undefined}
                  onClick={(e) => onNavigate?.(item.href, e)}
                >
                  <Icon name={item.icon} size={item.sub ? 'sm' : 'md'} />
                  {/* `title` no rótulo, não no link: o texto inteiro ao passar o mouse
                      só onde as reticências podem tê-lo cortado (`.lbl`). */}
                  <span className={s.lbl} title={item.label}>
                    {item.label}
                  </span>
                  {item.count ? (
                    <span
                      className={[s.cnt, item.quietCount ? s.cntQuiet : '']
                        .filter(Boolean)
                        .join(' ')}
                    >
                      {item.count}
                    </span>
                  ) : null}
                </a>
              );
            })}
          </div>
        ))}
      </nav>

      {footer.length > 0 ? (
        <div className={s.footwrap}>
          {footer.map((item) => (
            <a
              key={item.href}
              href={item.href}
              className={s.nav}
              target="_blank"
              // `noreferrer noopener` é a forma da casa (ver `MarkdownView`), e não
              // só `noopener`: o par é o que já existe no produto para link que sai.
              rel="noreferrer noopener"
            >
              <Icon name={item.icon} size="md" />
              <span className={s.lbl} title={item.label}>
                {item.label}
              </span>
            </a>
          ))}
        </div>
      ) : null}
    </div>
  );
}
